import { useEffect, useRef, useState } from 'react'
import AircallWorkspace from 'aircall-everywhere'
import { ReactComponent as CloseIcon } from '../assets/images/icons/ic-close.svg'
import aircallIcon from '../assets/images/aircall.png'
import TooltipWrapper from './TooltipWrapper'
import styles from './AircallWorkspacePanel.module.css'

const WORKSPACE_SELECTOR = '#aircall-workspace-v2'
const PANEL_LAYOUT_KEY = 'velv:aircall-workspace-layout'
const PANEL_MARGIN = 16
const PANEL_MIN_WIDTH = 320
const PANEL_MIN_HEIGHT = 480
const MOBILE_PANEL_BREAKPOINT = 700

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max))

const clampPanelRect = (rect) => {
    const maxWidth = Math.max(PANEL_MIN_WIDTH, window.innerWidth - (PANEL_MARGIN * 2))
    const maxHeight = Math.max(PANEL_MIN_HEIGHT, window.innerHeight - (PANEL_MARGIN * 2))
    const width = clamp(rect.width, PANEL_MIN_WIDTH, maxWidth)
    const height = clamp(rect.height, PANEL_MIN_HEIGHT, maxHeight)
    return {
        width,
        height,
        x: clamp(rect.x, PANEL_MARGIN, window.innerWidth - width - PANEL_MARGIN),
        y: clamp(rect.y, PANEL_MARGIN, window.innerHeight - height - PANEL_MARGIN),
    }
}

const resizePanelRect = (rect, direction, deltaX, deltaY) => {
    let left = rect.x
    let top = rect.y
    let right = rect.x + rect.width
    let bottom = rect.y + rect.height

    if (direction.includes('w')) {
        left = clamp(rect.x + deltaX, PANEL_MARGIN, right - PANEL_MIN_WIDTH)
    }
    if (direction.includes('e')) {
        right = clamp(
            rect.x + rect.width + deltaX,
            left + PANEL_MIN_WIDTH,
            window.innerWidth - PANEL_MARGIN,
        )
    }
    if (direction.includes('n')) {
        top = clamp(rect.y + deltaY, PANEL_MARGIN, bottom - PANEL_MIN_HEIGHT)
    }
    if (direction.includes('s')) {
        bottom = clamp(
            rect.y + rect.height + deltaY,
            top + PANEL_MIN_HEIGHT,
            window.innerHeight - PANEL_MARGIN,
        )
    }

    return {
        x: left,
        y: top,
        width: right - left,
        height: bottom - top,
    }
}

const getInitialPanelRect = (besideDrawer) => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(PANEL_LAYOUT_KEY))
        if (stored && ['x', 'y', 'width', 'height'].every((key) => Number.isFinite(stored[key]))) {
            return clampPanelRect(stored)
        }
    } catch {
        // Ignore invalid or unavailable local storage and use the default layout.
    }

    const width = Math.min(376, window.innerWidth - (PANEL_MARGIN * 2))
    const height = Math.min(700, window.innerHeight - (PANEL_MARGIN * 2))
    const drawerWidth = besideDrawer && window.innerWidth > 1100
        ? Math.min(620, window.innerWidth * 0.94) + PANEL_MARGIN
        : PANEL_MARGIN
    return clampPanelRect({
        width,
        height,
        x: window.innerWidth - width - drawerWidth,
        y: window.innerHeight - height - PANEL_MARGIN,
    })
}

const dialErrorMessage = (error) => {
    switch (error?.code) {
        case 'in_call':
            return 'Aircall è già impegnato in una chiamata.'
        case 'not_ready':
        case 'no_answer':
            return 'Accedi ad Aircall nel dialer prima di chiamare.'
        default:
            return error?.message || 'Non è stato possibile preparare la chiamata.'
    }
}

const AircallWorkspacePanel = ({
    mounted,
    open,
    besideDrawer,
    pendingDial,
    onClose,
    onOpen,
    onDialHandled,
}) => {
    const workspaceRef = useRef(null)
    const lastDialRequestRef = useRef(null)
    const interactionRef = useRef(null)
    const panelRectRef = useRef(null)
    const onOpenRef = useRef(onOpen)
    const onDialHandledRef = useRef(onDialHandled)
    const [ready, setReady] = useState(false)
    const [agentEmail, setAgentEmail] = useState('')
    const [message, setMessage] = useState('Accedi ad Aircall per usare il dialer integrato.')
    const [panelRect, setPanelRect] = useState(null)
    const [interaction, setInteraction] = useState(null)

    const updatePanelRect = (nextRect) => {
        const normalized = clampPanelRect(nextRect)
        panelRectRef.current = normalized
        setPanelRect(normalized)
    }

    const savePanelRect = () => {
        if (!panelRectRef.current) return
        try {
            window.localStorage.setItem(PANEL_LAYOUT_KEY, JSON.stringify(panelRectRef.current))
        } catch {
            // The layout remains usable even when local storage is unavailable.
        }
    }

    const startInteraction = (type, event) => {
        if (window.innerWidth <= MOBILE_PANEL_BREAKPOINT || !panelRectRef.current) return
        if (type === 'drag' && event.target.closest('button')) return
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        interactionRef.current = {
            type,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            rect: panelRectRef.current,
        }
        setInteraction(type)
    }

    const moveInteraction = (event) => {
        const current = interactionRef.current
        if (!current || current.pointerId !== event.pointerId) return
        const deltaX = event.clientX - current.startX
        const deltaY = event.clientY - current.startY
        if (current.type === 'drag') {
            updatePanelRect({
                ...current.rect,
                x: current.rect.x + deltaX,
                y: current.rect.y + deltaY,
            })
        } else if (current.type.startsWith('resize-')) {
            updatePanelRect(
                resizePanelRect(
                    current.rect,
                    current.type.replace('resize-', ''),
                    deltaX,
                    deltaY,
                ),
            )
        }
    }

    const endInteraction = (event) => {
        const current = interactionRef.current
        if (!current || current.pointerId !== event.pointerId) return
        interactionRef.current = null
        setInteraction(null)
        savePanelRect()
    }

    useEffect(() => {
        onOpenRef.current = onOpen
        onDialHandledRef.current = onDialHandled
    }, [onDialHandled, onOpen])

    useEffect(() => {
        if (!mounted || panelRectRef.current) return
        updatePanelRect(getInitialPanelRect(besideDrawer))
    }, [besideDrawer, mounted])

    useEffect(() => {
        if (!mounted) return undefined
        const handleResize = () => {
            if (window.innerWidth <= MOBILE_PANEL_BREAKPOINT || !panelRectRef.current) return
            updatePanelRect(panelRectRef.current)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [mounted])

    useEffect(() => {
        if (!mounted || workspaceRef.current) return undefined

        const workspace = new AircallWorkspace({
            domToLoadWorkspace: WORKSPACE_SELECTOR,
            size: 'auto',
            debug: process.env.NODE_ENV !== 'production',
            onLogin: (settings) => {
                setReady(true)
                setAgentEmail(settings?.user?.email || '')
                setMessage('Aircall è pronto.')
            },
            onLogout: () => {
                setReady(false)
                setAgentEmail('')
                setMessage('Accedi ad Aircall per usare il dialer integrato.')
            },
        })

        const handleIncomingCall = () => {
            setMessage('Chiamata in arrivo.')
            onOpenRef.current?.()
        }
        const handleOutgoingCall = () => {
            setMessage('Chiamata in uscita…')
            onOpenRef.current?.()
        }
        const handleOutgoingAnswered = () => setMessage('Chiamata in corso.')
        const handleCallEnded = () => setMessage('Chiamata terminata.')

        workspace.on('incoming_call', handleIncomingCall)
        workspace.on('outgoing_call', handleOutgoingCall)
        workspace.on('outgoing_answered', handleOutgoingAnswered)
        workspace.on('call_ended', handleCallEnded)
        workspaceRef.current = workspace

        return () => {
            workspace.removeListener('incoming_call')
            workspace.removeListener('outgoing_call')
            workspace.removeListener('outgoing_answered')
            workspace.removeListener('call_ended')
            workspaceRef.current = null
        }
    }, [mounted])

    useEffect(() => {
        if (!pendingDial || !ready || !workspaceRef.current) return
        if (lastDialRequestRef.current === pendingDial.id) return

        lastDialRequestRef.current = pendingDial.id
        setMessage('Preparazione della chiamata…')
        workspaceRef.current.send(
            'dial_number',
            { phone_number: pendingDial.phoneNumber },
            (success, data) => {
                if (success) {
                    setMessage('Numero pronto. Avvia la chiamata dal dialer Aircall.')
                } else {
                    setMessage(dialErrorMessage(data))
                }
                onDialHandledRef.current?.(success, data)
            },
        )
    }, [pendingDial, ready])

    return (
        <>
            {!open && (
                <TooltipWrapper text="Aircall">
                    <button
                        type="button"
                        className={styles.launcher}
                        aria-label="Aircall"
                        onClick={onOpen}
                    >
                        <img src={aircallIcon} alt="" />
                    </button>
                </TooltipWrapper>
            )}
            {mounted && (
                <aside
                    className={`${styles.panel} ${open ? styles.open : styles.closed} ${interaction ? styles.interacting : ''}`}
                    style={panelRect ? {
                        left: panelRect.x,
                        top: panelRect.y,
                        width: panelRect.width,
                        height: panelRect.height,
                    } : undefined}
                    aria-label="Aircall Workspace"
                    aria-hidden={!open}
                >
                    <header
                        className={styles.header}
                        onPointerDown={(event) => startInteraction('drag', event)}
                        onPointerMove={moveInteraction}
                        onPointerUp={endInteraction}
                        onPointerCancel={endInteraction}
                    >
                        <div>
                            <strong>Aircall Workspace</strong>
                            <span className={ready ? styles.ready : styles.notReady}>
                                {ready ? `Connesso${agentEmail ? ` · ${agentEmail}` : ''}` : 'Accesso richiesto'}
                            </span>
                        </div>
                        <button type="button" onClick={onClose} aria-label="Riduci Aircall Workspace">
                            <CloseIcon />
                        </button>
                    </header>
                    <div className={styles.message} aria-live="polite">{message}</div>
                    <div id="aircall-workspace-v2" className={styles.workspace} />
                    {['nw', 'ne', 'se', 'sw'].map((direction) => (
                        <div
                            className={`${styles.resizeHandle} ${styles[`resizeHandle_${direction}`]}`}
                            key={direction}
                            onPointerDown={(event) => startInteraction(`resize-${direction}`, event)}
                            onPointerMove={moveInteraction}
                            onPointerUp={endInteraction}
                            onPointerCancel={endInteraction}
                            aria-hidden="true"
                        />
                    ))}
                </aside>
            )}
        </>
    )
}

export default AircallWorkspacePanel
